<template>
    <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-3">
            <p
                class="text-theme-muted text-xs font-semibold uppercase tracking-wider"
            >
                {{ title }}
            </p>
            <custom-checkbox
                :value="offerActive"
                :disabled="!enabled"
                warn
                @input="onToggleOffer"
            >
                Poner en oferta
            </custom-checkbox>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
                <input-money-component
                    :item-current="value.priceOffer"
                    :enabled="enabled"
                    label="Precio de venta"
                    icon="💲"
                    :message-error="saleError"
                    @input="onInputSalePrice"
                    @blur="onBlur('salePrice')"
                />
                <span
                    v-if="currentPriceOffer !== null"
                    class="self-start text-xs px-2 py-0.5 rounded-full bg-theme-hover text-theme-muted whitespace-nowrap"
                >
                    Actual: {{ currentPriceOffer | currency }}
                </span>
            </div>

            <div v-if="offerActive" class="flex flex-col gap-1">
                <input-money-component
                    :item-current="value.price"
                    :enabled="enabled"
                    label="Precio anterior"
                    icon="💲"
                    :message-error="offerErrorMessage"
                    @input="onInputPreviousPrice"
                    @blur="onBlur('previousPrice')"
                />
                <span v-if="discount" class="text-sm text-theme-warning">
                    Descuento: {{ discount }}%
                </span>
                <span
                    v-if="currentPrice !== null"
                    class="self-start text-xs px-2 py-0.5 rounded-full bg-theme-hover text-theme-muted whitespace-nowrap"
                >
                    Actual: {{ currentPrice | currency }}
                </span>
            </div>

            <div v-if="showCost" class="flex flex-col gap-1">
                <input-money-component
                    :item-current="value.cost"
                    :enabled="enabled"
                    label="Costo"
                    icon="💲"
                    @input="onInputCost"
                    @blur="onBlur('cost')"
                />
                <p v-if="costWarning" class="text-xs text-theme-warning">
                    {{ costWarning }}
                </p>
                <span
                    v-if="currentCost !== null"
                    class="self-start text-xs px-2 py-0.5 rounded-full bg-theme-hover text-theme-muted whitespace-nowrap"
                >
                    Actual: {{ currentCost | currency }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import {
    SALE_PRICE_REQUIRED_ERROR,
    getCostWarning,
    getDiscountPercent,
    getOfferError,
    isOfferPrice
} from "@/common/lib/price-offer";

import CustomCheckbox from "@/components/utils/CustomCheckbox.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";

const samePrices = (a, b) =>
    !!a &&
    !!b &&
    a.price === b.price &&
    a.priceOffer === b.priceOffer &&
    a.cost === b.cost;

export default {
    name: "PriceOfferFields",
    components: { CustomCheckbox, InputMoneyComponent },
    props: {
        value: {
            type: Object,
            default: () => ({ price: 0, priceOffer: 0, cost: 0 })
        },
        title: { type: String, default: "Precios" },
        enabled: { type: Boolean, default: true },
        showCost: { type: Boolean, default: true },
        showErrors: { type: Boolean, default: false },
        currentPrice: { type: Number, default: null },
        currentPriceOffer: { type: Number, default: null },
        currentCost: { type: Number, default: null }
    },
    emits: ["input", "blur", "error-change"],
    data: () => ({
        offerActive: false,
        lastEmitted: null,
        touched: { salePrice: false, previousPrice: false }
    }),
    computed: {
        offerError() {
            return getOfferError({
                salePrice: this.value.priceOffer,
                previousPrice: this.value.price,
                offerActive: this.offerActive
            });
        },
        saleError() {
            return !this.value.priceOffer &&
                (this.showErrors || this.touched.salePrice)
                ? SALE_PRICE_REQUIRED_ERROR
                : "";
        },
        offerErrorMessage() {
            return this.showErrors || this.touched.previousPrice
                ? this.offerError
                : "";
        },
        hasError() {
            return !this.value.priceOffer || !!this.offerError;
        },
        discount() {
            return this.offerActive
                ? getDiscountPercent(this.value.price, this.value.priceOffer)
                : 0;
        },
        costWarning() {
            return getCostWarning({
                salePrice: this.value.priceOffer,
                previousPrice: this.value.price,
                cost: this.value.cost,
                offerActive: this.offerActive
            });
        }
    },
    watch: {
        value: {
            handler(value) {
                if (samePrices(value, this.lastEmitted)) return;
                this.deriveOfferState();
            },
            deep: true
        },
        hasError: {
            handler(value) {
                this.$emit("error-change", value);
            },
            immediate: true
        }
    },
    mounted() {
        this.deriveOfferState();
    },
    methods: {
        deriveOfferState() {
            const { price, priceOffer } = this.value;
            this.offerActive = isOfferPrice(price, priceOffer);
            if (!this.offerActive && priceOffer > price) {
                this.emitPrices({ price: priceOffer });
            }
        },
        emitPrices(patch) {
            this.lastEmitted = { ...this.value, ...patch };
            this.$emit("input", this.lastEmitted);
        },
        onInputSalePrice(value) {
            this.emitPrices({
                priceOffer: value,
                ...(this.offerActive ? {} : { price: value })
            });
        },
        onInputPreviousPrice(value) {
            this.emitPrices({ price: value });
        },
        onInputCost(value) {
            this.emitPrices({ cost: value });
        },
        onToggleOffer(active) {
            this.offerActive = active;
            if (!active) {
                this.emitPrices({ price: this.value.priceOffer });
            }
            this.$emit("blur");
        },
        onBlur(field) {
            if (field in this.touched) this.touched[field] = true;
            this.$emit("blur");
        }
    }
};
</script>
